import { withDependencies } from '@wix/thunderbolt-ioc'
import { FetchFn } from '@wix/thunderbolt-becky-types'
import { Catharsis, CatharsisArgs, CatharsisSymbol } from '@wix/thunderbolt-catharsis'
import { CSMStoreSymbol, CSMStoreApi } from 'ds-feature-viewer-manager-renderer-model-api'
import { getBuilderComponentUrl } from '@wix/thunderbolt-becky-root'
import { ViewerClientSpecMap } from '@wix/thunderbolt-symbols'
import { AsyncRunAndReport } from '@wix/thunderbolt-commons'
import { PresetInvalidationApi } from '../types'

export type PresetData = {
	widgetId: string
	appDefinitionId: string
	presetId: string
}

export type PresetsDataList = Array<PresetData>

export const createFetchMemoization = (fetchFn: FetchFn) => {
	const fetchPromises: { [url: string]: Promise<Response> } = {}
	const memoizedFetch: FetchFn = (url: string) => {
		if (!fetchPromises[url]) {
			fetchPromises[url] = fetchFn(url)
		}
		return fetchPromises[url]
	}
	return memoizedFetch
}

export const PresetsInvalidationApi = withDependencies(
	[CSMStoreSymbol, CatharsisSymbol],
	(csmStore: CSMStoreApi, catharsis: Catharsis): PresetInvalidationApi => {
		const getOnInvalidation = ({
			fetchFn,
			runAndReport,
		}: {
			fetchFn: FetchFn
			runAndReport: AsyncRunAndReport
		}) => {
			const fetchMemo = createFetchMemoization(fetchFn)
			const fetchPresetCss = ({ appDefinitionId, widgetId, presetId }: PresetData) => {
				const csm = (csmStore.get() as unknown) as ViewerClientSpecMap
				const app = csm[appDefinitionId]
				const componentUrl = getBuilderComponentUrl(app, widgetId)

				return fetchMemo(`${componentUrl}/${widgetId}/${presetId}`)
					.then((res) => {
						if (!res.ok) {
							throw new Error('Fetching preset CSS failed')
						}
						return res.text()
					})
					.catch((e) => {
						throw new Error(
							`Failed to fetch presets for widget: ${widgetId}, preset: ${presetId}. Original Error - ${e.message}`
						)
					})
			}

			const presetsMemo = new Set<string>()
			const onPresetInvalidation: CatharsisArgs['onInvalidation'] = (path, get) => {
				const [, aspect] = path
				if (aspect === 'presetIds') {
					const presetDataList = get<PresetsDataList | undefined>(path)
					if (!presetDataList) {
						return
					}
					if (presetDataList) {
						presetDataList.forEach((presetData) => {
							const presetKey = JSON.stringify(presetData)
							if (!presetsMemo.has(presetKey)) {
								presetsMemo.add(presetKey)
								const { appDefinitionId, widgetId, presetId } = presetData
								const presetStore = get(['presetsCss', 'widgets', widgetId])
								runAndReport('fetchPresetCss', () =>
									fetchPresetCss({ appDefinitionId, widgetId, presetId }).then((cssJSON) => {
										presetStore[presetId] = cssJSON
										catharsis.transaction((ops) => ops.updatePresetCss(widgetId, presetStore))
									})
								)
							}
						})
					}
				}
			}

			return onPresetInvalidation
		}

		return {
			getOnInvalidation,
		}
	}
)
